import { useMemo, useRef, useState } from 'react';
import { colorOf } from '../lib/colors';
import './TagField.css';

export interface TagOption {
  id: string;
  name: string;
  color: string | null;
}

interface Props {
  /** Ids currently on the item. */
  value: string[];
  /** Everything selectable in this scope. */
  options: TagOption[];
  placeholder: string;
  /** Shown in place of the field when the scope has nothing to offer yet. */
  emptyHint?: string;
  disabled?: boolean;
  onChange: (ids: string[]) => void;
  /** Creates a tag with the typed name and applies it. */
  onCreate: (name: string) => void;
  onEdit: (option: TagOption) => void;
}

const MAX_SUGGESTIONS = 8;

/**
 * Tag picker with a dropdown of what already exists.
 *
 * Typing filters; Enter takes the highlighted suggestion, or creates the typed
 * name when nothing matches. Tags are ids here, not strings — the name is only
 * ever a label, so renaming one never has to touch a single item.
 */
export function TagField({
  value,
  options,
  placeholder,
  emptyHint,
  disabled = false,
  onChange,
  onCreate,
  onEdit,
}: Props) {
  const [draft, setDraft] = useState('');
  const [open, setOpen] = useState(false);
  const [cursor, setCursor] = useState(0);
  const blurTimer = useRef<number>(0);

  const byId = useMemo(() => new Map(options.map((o) => [o.id, o])), [options]);

  const suggestions = useMemo(() => {
    const q = draft.trim().toLowerCase().replace(/^[#@]/, '');
    return options
      .filter((o) => !value.includes(o.id))
      .filter((o) => !q || o.name.toLowerCase().includes(q))
      .sort((a, b) => {
        const aStarts = a.name.toLowerCase().startsWith(q);
        const bStarts = b.name.toLowerCase().startsWith(q);
        if (aStarts !== bStarts) return aStarts ? -1 : 1;
        return a.name.localeCompare(b.name);
      })
      .slice(0, MAX_SUGGESTIONS);
  }, [draft, options, value]);

  const clean = draft.trim().replace(/^[#@]/, '');
  const isNew =
    clean.length > 0 && !options.some((o) => o.name.toLowerCase() === clean.toLowerCase());

  const add = (id: string) => {
    if (!value.includes(id)) onChange([...value, id]);
    setDraft('');
    setCursor(0);
  };

  function onKeyDown(e: React.KeyboardEvent<HTMLInputElement>) {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setOpen(true);
      setCursor((c) => Math.min(suggestions.length - (isNew ? 0 : 1), c + 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setCursor((c) => Math.max(0, c - 1));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      const picked = suggestions[cursor];
      if (picked) add(picked.id);
      else if (clean) {
        onCreate(clean);
        setDraft('');
      }
    } else if (e.key === 'Escape') {
      e.stopPropagation();
      if (open) setOpen(false);
      else setDraft('');
    } else if (e.key === 'Backspace' && !draft && value.length) {
      onChange(value.slice(0, -1));
    }
  }

  if (disabled) {
    return <p className="tagf__disabled">{emptyHint}</p>;
  }

  return (
    <div className="tagf">
      {value.length > 0 && (
        <div className="tagf__chips">
          {value.map((id) => {
            const option = byId.get(id);
            // An id with no row behind it means the tag was deleted on another
            // device and this item has not caught up. Showing it as unknown
            // beats silently dropping something the user put there.
            const hex = colorOf(option?.color);
            return (
              <span className="chip tagf__chip" key={id}>
                <span
                  className="tagf__dot"
                  style={{ background: hex ?? 'var(--border-strong)' }}
                  aria-hidden="true"
                />
                <button
                  type="button"
                  className="tagf__name"
                  title={option ? `Edit ${option.name}` : 'Unknown tag'}
                  onClick={() => option && onEdit(option)}
                >
                  {option?.name ?? 'unknown'}
                </button>
                <button
                  type="button"
                  className="chip__x"
                  aria-label={`Remove ${option?.name ?? 'tag'}`}
                  onClick={() => onChange(value.filter((t) => t !== id))}
                >
                  ✕
                </button>
              </span>
            );
          })}
        </div>
      )}

      <div className="tagf__inputWrap">
        <input
          className="field__input"
          value={draft}
          placeholder={placeholder}
          spellCheck={false}
          aria-label={placeholder}
          aria-expanded={open}
          role="combobox"
          aria-controls="tag-suggestions"
          onChange={(e) => {
            setDraft(e.target.value);
            setOpen(true);
            setCursor(0);
          }}
          onFocus={() => setOpen(true)}
          // Delayed so a click on a suggestion lands before the list unmounts.
          onBlur={() => {
            blurTimer.current = window.setTimeout(() => setOpen(false), 120);
          }}
          onKeyDown={onKeyDown}
        />

        {open && (suggestions.length > 0 || isNew) && (
          <div className="tagf__menu" id="tag-suggestions" role="listbox">
            {suggestions.map((option, i) => {
              const hex = colorOf(option.color);
              return (
                <button
                  key={option.id}
                  type="button"
                  role="option"
                  aria-selected={cursor === i}
                  className={`tagf__option${cursor === i ? ' is-active' : ''}`}
                  onMouseEnter={() => setCursor(i)}
                  onMouseDown={() => window.clearTimeout(blurTimer.current)}
                  onClick={() => add(option.id)}
                >
                  <span
                    className="tagf__dot"
                    style={{ background: hex ?? 'var(--border-default)' }}
                    aria-hidden="true"
                  />
                  <span className="tagf__optionName truncate">{option.name}</span>
                </button>
              );
            })}

            {isNew && (
              <button
                type="button"
                className="tagf__option tagf__option--create"
                onMouseDown={() => window.clearTimeout(blurTimer.current)}
                onClick={() => {
                  setOpen(false);
                  setDraft('');
                  onCreate(clean);
                }}
              >
                <span className="tagf__plus" aria-hidden="true">
                  +
                </span>
                <span className="truncate">
                  Create <span className="tagf__new">{clean}</span>…
                </span>
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
